import { ParsedOffer } from './types';
import { editOffer, generateSdpFragment, linkToIceServers, parseOffer } from './util';

const RESTART_PAUSE = 2000;

export class WHEPClient {
  video: HTMLVideoElement;
  pc: RTCPeerConnection | null;
  restartTimeout: number | null;
  sessionUrl: string;
  queuedCandidates: RTCIceCandidate[];
  offerData: ParsedOffer | undefined;
  url: string;
  onTrack: (event: RTCTrackEvent) => void;
  onError: (error: string) => void;
  automaticRestart = false;

  constructor({
    video,
    url,
    onTrack,
    onError,
  }: {
    video: HTMLVideoElement;
    url: string;
    onTrack: (event: RTCTrackEvent) => void;
    onError: (error: string) => void;
  }) {
    this.video = video;
    this.pc = null;
    this.restartTimeout = null;
    this.sessionUrl = '';
    this.queuedCandidates = [];
    this.url = url;
    this.onTrack = onTrack;
    this.onError = onError;
    this.start();
  }

  start(): void {
    fetch(new URL('whep', this.url), {
      method: 'OPTIONS',
    })
      .then((res) => this.onIceServers(res))
      .catch((err) => {
        console.error('error: ' + err);
        this.scheduleRestart(err);
      });
  }

  onIceServers(res: Response): void {
    this.pc = new RTCPeerConnection({
      iceServers: linkToIceServers(res.headers.get('Link')),
    });

    const direction = 'sendrecv';
    this.pc.addTransceiver('video', { direction });
    this.pc.addTransceiver('audio', { direction });

    this.pc.onicecandidate = (evt) => this.onLocalCandidate(evt);
    this.pc.oniceconnectionstatechange = () => this.onConnectionState();

    this.pc.ontrack = (evt) => {
      this.onTrack(evt);
      this.video.srcObject = evt.streams[0];
    };

    this.pc.createOffer().then((offer) => this.onLocalOffer(offer));
  }

  onLocalOffer(offer: RTCSessionDescriptionInit): void {
    editOffer(offer);

    this.offerData = parseOffer(offer.sdp);
    if (this.pc) {
      this.pc.setLocalDescription(offer);
    }
    fetch(new URL('whep', this.url), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/sdp',
      },
      body: offer.sdp,
    })
      .then((res) => {
        if (res.status !== 201) {
          throw new Error('bad status code');
        }
        this.sessionUrl = new URL(res.headers.get('location') || '', this.url).toString();
        return res.text();
      })
      .then((sdp) =>
        this.onRemoteAnswer(
          new RTCSessionDescription({
            type: 'answer',
            sdp,
          })
        )
      )
      .catch((err) => {
        console.error('error: ' + err);
        this.scheduleRestart(err);
      });
  }

  onConnectionState(): void {
    if (this.restartTimeout !== null) {
      return;
    }
    switch (this.pc?.iceConnectionState) {
      case 'disconnected':
        this.scheduleRestart('disconnected');
    }
  }

  onRemoteAnswer(answer: RTCSessionDescription): void {
    if (this.restartTimeout !== null) {
      return;
    }

    if (this.pc) {
      this.pc.setRemoteDescription(answer);
    }

    if (this.queuedCandidates.length !== 0) {
      this.sendLocalCandidates(this.queuedCandidates);
      this.queuedCandidates = [];
    }
  }

  onLocalCandidate(evt: RTCPeerConnectionIceEvent): void {
    if (this.restartTimeout !== null) {
      return;
    }

    if (evt.candidate !== null) {
      if (this.sessionUrl === '') {
        this.queuedCandidates.push(evt.candidate);
      } else {
        this.sendLocalCandidates([evt.candidate]);
      }
    }
  }

  sendLocalCandidates(candidates: RTCIceCandidate[]): void {
    if (!this.offerData) {
      return;
    }
    fetch(this.sessionUrl, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/trickle-ice-sdpfrag',
        'If-Match': '*',
      },
      body: generateSdpFragment(this.offerData, candidates),
    })
      .then((res) => {
        if (res.status !== 204) {
          throw new Error('bad status code');
        }
      })
      .catch((err) => {
        console.error('error: ' + err);
        this.scheduleRestart(err);
      });
  }

  scheduleRestart(err: any): void {
    // check if error is a string
    if (typeof err === 'string') {
      return this.onError(err);
    } else {
      this.onError(err.message ?? JSON.stringify(err));
    }
    if (!this.automaticRestart) {
      return;
    }

    if (this.restartTimeout !== null) {
      return;
    }

    if (this.pc !== null) {
      this.pc.close();
      this.pc = null;
    }

    this.restartTimeout = window.setTimeout(() => {
      this.restartTimeout = null;
      this.start();
    }, RESTART_PAUSE);

    if (this.sessionUrl) {
      fetch(this.sessionUrl, {
        method: 'DELETE',
      })
        .then((res) => {
          if (res.status !== 200) {
            throw new Error('bad status code');
          }
        })
        .catch((err) => {
          console.error('delete session error: ' + err);
        });
    }
    this.sessionUrl = '';

    this.queuedCandidates = [];
  }
}
